// A súgó-asszisztens widgetje: indító gomb, panel, kérdés küldése, a streamelt válasz
// megjelenítése a forrásokkal, visszajelzés. A logika DOM nélküli része (SSE, modell,
// linkek, mentés) az assistant-core.ts-ben van; a háttér szerződése a
// Web\Api\HelpAssistant\README.md-ben. A megjelenés a megosztott design-system.css
// .cs-chat* komponense.
//
// Csak akkor indul, ha a sablon kirakta a gyökérelemet (#assistant) — ezt a build
// kapcsolja (webpack.config.js "assistantEnabled") —, és csak akkor jelenik meg, ha a
// háttér tud válaszolni (lásd setupAssistant()).
import { parseMarkdown, sanitizeHtml } from "../../../../../Brand/Web/ts/markdown";
import { applyEvent, citedSources, classifyLink, createConversation, describeHttpError, failAnswer, isAnswer, parseHelpRoute, restoreConversation, serializeConversation, sourceLabel, splitCitations, SseDecoder, startAnswer, } from "./assistant-core";
const storageKey = "cobra-assistant";
const maxQuestionChars = 1000;
const statusTimeoutMs = 5000;
// Ezen a szélességen a panel teljes képernyős; egy forrásra kattintva bezárul, hogy a
// megnyitott oldal látsszon. Ugyanez a határ a design-system.css .cs-chat szabályában.
const fullScreenQuery = "(max-width: 640px)";
// A válasz HTML-jéből ami kimarad: a modell a rendszerprompt szerint csak bekezdést,
// listát és kiemelést ír, de ezt nem lehet kikényszeríteni.
const forbiddenTags = ["form", "iframe", "script", "style", "img", "picture", "video", "audio", "table", "input", "button", "textarea", "select", "svg"];
const forbiddenAttributes = ["style", "class", "id"];
const panelTemplate = `
  <button type="button" class="cs-chat-launcher" data-role="launcher" aria-controls="assistant-panel" aria-expanded="false">
    <svg class="cs-chat-launcher__icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>
    <span>Kérdezzen a súgótól</span>
  </button>
  <section id="assistant-panel" class="cs-chat cs-scroll-panel" data-role="panel" role="dialog" aria-modal="false" aria-labelledby="assistant-title" hidden>
    <header class="cs-chat__head cs-scroll-panel__head">
      <div class="cs-chat__titles">
        <h2 id="assistant-title" class="cs-chat__title">Súgó asszisztens</h2>
        <p class="cs-chat__context" data-role="context"></p>
      </div>
      <button type="button" class="cs-chat__icon-btn" data-role="reset" aria-label="Új beszélgetés" title="Új beszélgetés">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4h4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button type="button" class="cs-chat__icon-btn" data-role="close" aria-label="Bezárás" title="Bezárás">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
      </button>
    </header>
    <div class="cs-chat__body cs-scroll-panel__body" data-role="log">
      <div class="cs-chat__intro">
        <p>Kérdezzen a program használatáról: a súgó leírásaiból válaszolok, és megmutatom, melyik oldalon olvashat róla bővebben.</p>
        <p class="cs-chat__fineprint">A válaszokat mesterséges intelligencia (Anthropic Claude) állítja össze, és tévedhet — ellenőrizze a hivatkozott oldalon. Kérem, ne írjon a kérdésbe személyes vagy céges adatot.</p>
      </div>
      <div data-role="entries"></div>
    </div>
    <form class="cs-chat__form cs-scroll-panel__head" data-role="form">
      <label class="visually-hidden" for="assistant-question">Kérdés</label>
      <textarea id="assistant-question" class="cs-chat__input" data-role="input" rows="2" maxlength="${maxQuestionChars}" placeholder="Írja ide a kérdését…" enterkeyhint="send"></textarea>
      <button type="submit" class="cs-btn cs-btn-accent cs-chat__send" data-role="send">Küldés</button>
    </form>
    <div class="visually-hidden" data-role="status" role="status" aria-live="polite"></div>
  </section>`;
const thumbUp = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 11v9H4v-9h3zm0 0l4-7a2 2 0 0 1 2 2v4h5a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 16.8 20H7" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>';
const thumbDown = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17 13V4h3v9h-3zm0 0l-4 7a2 2 0 0 1-2-2v-4H6a2 2 0 0 1-2-2.3l1.2-6A2 2 0 0 1 7.2 4H17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/></svg>';
// A widget csak akkor épül fel, ha a háttér tud válaszolni: a health (GET /api/status/health,
// ugyanaz a végpont, mint a többi Web\Api szolgáltatásé) "ok"-t ad, és nem fogyott el a napi
// keret. Leállt háttérnél (az nginx 502-je, a fejlesztői proxy hibája), be nem kötött
// health-nél (404), 503-nál (nincs tartalom vagy kulcs), elfogyott keretnél és
// időtúllépésnél a gyökérelem üres marad, és undefined jön vissza.
export async function setupAssistant(root, host) {
    const apiBase = root.dataset.api ?? "/api/assist";
    const healthUrl = root.dataset.health ?? "/api/status/health";
    return (await isAvailable(healthUrl)) ? buildAssistant(root, host, apiBase) : undefined;
}
async function isAvailable(healthUrl) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), statusTimeoutMs);
    try {
        const response = await fetch(healthUrl, { cache: "no-store", signal: controller.signal });
        if (!response.ok) {
            return false;
        }
        // A törzset is nézi: egy rosszul beállított szerver 200-zal a lap HTML-jét is adhatná.
        const health = (await response.json());
        return health.status === "ok" && health.budgetExhausted !== true;
    }
    catch {
        return false;
    }
    finally {
        window.clearTimeout(timer);
    }
}
function buildAssistant(root, host, apiBase) {
    root.innerHTML = panelTemplate;
    const element = (role) => root.querySelector(`[data-role="${role}"]`);
    const launcher = element("launcher");
    const panel = element("panel");
    const contextLine = element("context");
    const log = element("log");
    const entriesContainer = element("entries");
    const form = element("form");
    const input = element("input");
    const sendButton = element("send");
    const status = element("status");
    let conversation = restoreConversation(readStorage()) ?? createConversation();
    let controller;
    let renderScheduled = false;
    const rendered = new Map();
    function readStorage() {
        try {
            return sessionStorage.getItem(storageKey);
        }
        catch {
            return null;
        }
    }
    function persist() {
        try {
            sessionStorage.setItem(storageKey, serializeConversation(conversation));
        }
        catch {
            // Privát ablak, letiltott tárolás: a beszélgetés csak a lap frissítéséig él.
        }
    }
    function isOpen() {
        return !panel.hidden;
    }
    function open(prefill) {
        panel.hidden = false;
        launcher.hidden = true;
        launcher.setAttribute("aria-expanded", "true");
        refreshContext();
        if (prefill) {
            input.value = prefill.slice(0, maxQuestionChars);
        }
        scrollToEnd();
        input.focus();
    }
    function close() {
        const hadFocus = panel.contains(document.activeElement);
        panel.hidden = true;
        launcher.hidden = false;
        launcher.setAttribute("aria-expanded", "false");
        if (hadFocus) {
            launcher.focus();
        }
    }
    function refreshContext() {
        const context = host.getContext();
        contextLine.textContent = context ? `${context.productLabel} · ${context.versionLabel}` : "Válasszon programverziót.";
    }
    function reset() {
        controller?.abort();
        conversation = createConversation();
        rendered.clear();
        entriesContainer.replaceChildren();
        persist();
        input.focus();
    }
    function setBusy(busy) {
        sendButton.disabled = busy;
        form.setAttribute("aria-busy", busy ? "true" : "false");
    }
    function scrollToEnd() {
        log.scrollTop = log.scrollHeight;
    }
    function isNearEnd() {
        return log.scrollHeight - log.scrollTop - log.clientHeight < 80;
    }
    // ------------------------------------------------------------ küldés --
    async function send() {
        const question = input.value.trim().slice(0, maxQuestionChars);
        if (question === "" || controller) {
            return;
        }
        const context = host.getContext();
        if (!context) {
            announce("Előbb válasszon programverziót a bal oldalon.");
            return;
        }
        // Egy beszélgetés egy termékről szól: termékváltás után új kezdődik.
        if (conversation.productId && conversation.productId !== context.product) {
            conversation.id = undefined;
            addNote(`Új beszélgetés — ${context.productLabel}`);
        }
        conversation.productId = context.product;
        input.value = "";
        const current = conversation;
        const answer = startAnswer(conversation, question);
        renderEntry(answer);
        scrollToEnd();
        setBusy(true);
        try {
            await ask(answer, context, true);
        }
        finally {
            controller = undefined;
            setBusy(false);
            // Közben "Új beszélgetés" volt: a régi válasz már nem tartozik ide.
            if (conversation === current) {
                renderEntry(answer);
                persist();
                announce(answer.status === "error" ? answer.error?.message ?? "" : "Megérkezett a válasz.");
            }
        }
    }
    async function ask(answer, context, mayRestart) {
        controller = new AbortController();
        let response;
        try {
            response = await fetch(`${apiBase}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    conversationId: conversation.id ?? null,
                    product: context.product,
                    version: context.version,
                    page: context.page ?? null,
                    question: answer.question,
                }),
                signal: controller.signal,
            });
        }
        catch {
            failAnswer(answer, controller.signal.aborted
                ? { code: "interrupted", message: "A válasz megszakadt." }
                : { code: "network", message: "Az asszisztens nem érhető el. Ellenőrizze a hálózati kapcsolatot." });
            return;
        }
        if (!response.ok) {
            const error = describeHttpError(response.status, await response.json().catch(() => undefined));
            // A beszélgetés a háttérben lejárt (tétlenség, újraindítás): a kérdés egy új
            // beszélgetés első kérdéseként megy tovább.
            if (error.code === "conversation_expired" && mayRestart) {
                conversation.id = undefined;
                insertNoteBefore(answer, "Az előző beszélgetés lejárt — új kezdődött.");
                await ask(answer, context, false);
                return;
            }
            failAnswer(answer, error);
            return;
        }
        const reader = response.body?.getReader();
        if (!reader) {
            failAnswer(answer, { code: "network", message: "A válasz nem olvasható." });
            return;
        }
        const decoder = new TextDecoder();
        const events = new SseDecoder();
        try {
            for (;;) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                for (const message of events.push(decoder.decode(value, { stream: true }))) {
                    applyEvent(conversation, answer, message);
                }
                scheduleRender(answer);
            }
        }
        catch {
            // A stream közben szakadt meg (hálózat, vagy a látogató új beszélgetést kezdett).
        }
        if (answer.status === "waiting" || answer.status === "streaming") {
            failAnswer(answer, { code: "interrupted", message: "A válasz megszakadt. Kérem, kérdezzen újra." });
        }
    }
    async function sendFeedback(answer, rating) {
        if (!conversation.id || answer.turn === undefined || answer.rating) {
            return;
        }
        answer.rating = rating;
        renderEntry(answer);
        try {
            const response = await fetch(`${apiBase}/feedback`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ conversationId: conversation.id, turn: answer.turn, rating }),
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            persist();
            announce("Köszönjük a visszajelzést.");
        }
        catch {
            // Lejárt beszélgetés vagy hálózati hiba: a gombok újra használhatók.
            answer.rating = undefined;
            renderEntry(answer);
        }
    }
    function addNote(note) {
        const entry = { note };
        conversation.entries.push(entry);
        renderEntry(entry);
    }
    function insertNoteBefore(answer, note) {
        const entry = { note };
        conversation.entries.splice(conversation.entries.indexOf(answer), 0, entry);
        const element = renderEntry(entry);
        const answerElement = rendered.get(answer);
        if (answerElement) {
            entriesContainer.insertBefore(element, answerElement);
        }
    }
    function announce(message) {
        status.textContent = message;
    }
    // -------------------------------------------------------- renderelés --
    function scheduleRender(answer) {
        if (renderScheduled) {
            return;
        }
        renderScheduled = true;
        requestAnimationFrame(() => {
            renderScheduled = false;
            if (!conversation.entries.includes(answer)) {
                return;
            }
            const follow = isNearEnd();
            renderEntry(answer);
            if (follow) {
                scrollToEnd();
            }
        });
    }
    function renderEntry(entry) {
        let element = rendered.get(entry);
        if (!element) {
            element = document.createElement("div");
            rendered.set(entry, element);
            entriesContainer.append(element);
        }
        if (isAnswer(entry)) {
            renderAnswer(element, entry);
        }
        else {
            element.className = "cs-chat__note";
            element.textContent = entry.note;
        }
        return element;
    }
    function renderAnswer(element, answer) {
        element.className = "cs-chat__turn";
        const question = document.createElement("div");
        question.className = "cs-chat__msg cs-chat__msg--user";
        question.textContent = answer.question;
        const reply = document.createElement("div");
        reply.className = "cs-chat__msg cs-chat__msg--bot";
        if (answer.markdown !== "") {
            reply.append(renderMarkdown(answer));
        }
        else if (answer.status === "waiting" || answer.status === "streaming") {
            reply.append(typingIndicator());
        }
        const sources = citedSources(conversation, answer);
        if (sources.length > 0 && answer.status !== "waiting") {
            const block = document.createElement("div");
            block.className = "cs-chat__sources";
            const title = document.createElement("p");
            title.className = "cs-chat__sources-title";
            title.textContent = "Források";
            const list = document.createElement("ol");
            for (const { source } of sources) {
                const item = document.createElement("li");
                const link = document.createElement("a");
                link.className = "cs-chat__source";
                link.href = source.href;
                link.textContent = sourceLabel(source);
                item.append(link);
                list.append(item);
            }
            block.append(title, list);
            reply.append(block);
        }
        if (answer.status === "error" && answer.error) {
            const error = document.createElement("p");
            error.className = "cs-chat__error";
            error.setAttribute("role", "alert");
            error.textContent = answer.error.message;
            reply.append(error);
        }
        else if (answer.status === "done") {
            if (answer.stopReason === "max_tokens") {
                const cut = document.createElement("p");
                cut.className = "cs-chat__error";
                cut.textContent = "A válasz túl hosszú lett, ezért itt megszakadt.";
                reply.append(cut);
            }
            reply.append(metaLine(answer));
        }
        element.replaceChildren(question, reply);
    }
    function typingIndicator() {
        const typing = document.createElement("p");
        typing.className = "cs-chat__typing";
        typing.innerHTML = '<span></span><span></span><span></span><span class="visually-hidden">A válasz készül…</span>';
        return typing;
    }
    function metaLine(answer) {
        const meta = document.createElement("div");
        meta.className = "cs-chat__meta";
        const note = document.createElement("span");
        note.textContent = answer.rating ? "Köszönjük a visszajelzést." : "AI által generált válasz — ellenőrizze a hivatkozott oldalon.";
        meta.append(note);
        if (answer.turn !== undefined) {
            const rate = document.createElement("span");
            rate.className = "cs-chat__rate";
            for (const [rating, label, icon] of [["up", "Hasznos volt", thumbUp], ["down", "Nem segített", thumbDown]]) {
                const button = document.createElement("button");
                button.type = "button";
                button.className = "cs-chat__icon-btn";
                button.dataset.rating = rating;
                button.setAttribute("aria-label", label);
                button.title = label;
                button.setAttribute("aria-pressed", answer.rating === rating ? "true" : "false");
                button.disabled = answer.rating !== undefined;
                button.innerHTML = icon;
                rate.append(button);
            }
            meta.append(rate);
        }
        return meta;
    }
    // A markdown → biztonságos HTML, a linkek szűrésével és a citációs jelölők cseréjével.
    function renderMarkdown(answer) {
        const container = document.createElement("div");
        container.className = "cs-chat__answer";
        container.innerHTML = sanitizeHtml(parseMarkdown(answer.markdown), { forbidTags: forbiddenTags, forbidAttr: forbiddenAttributes });
        for (const link of Array.from(container.querySelectorAll("a"))) {
            const kind = classifyLink(link.getAttribute("href"), window.location.origin);
            if (kind === "external") {
                link.target = "_blank";
                link.rel = "noopener";
            }
            else if (kind !== "internal") {
                link.replaceWith(...Array.from(link.childNodes));
            }
        }
        const sources = citedSources(conversation, answer);
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        while (walker.nextNode()) {
            textNodes.push(walker.currentNode);
        }
        for (const node of textNodes) {
            const pieces = splitCitations(node.data);
            if (!pieces.some((piece) => typeof piece === "number")) {
                continue;
            }
            const parts = [];
            for (const piece of pieces) {
                if (typeof piece === "string") {
                    parts.push(document.createTextNode(piece));
                    continue;
                }
                const number = piece;
                const source = sources.find((entry) => entry.number === number)?.source;
                const sup = document.createElement("sup");
                sup.className = "cs-chat__cite";
                if (source) {
                    const link = document.createElement("a");
                    link.href = source.href;
                    link.textContent = String(number);
                    link.title = sourceLabel(source);
                    link.setAttribute("aria-label", `${number}. forrás: ${sourceLabel(source)}`);
                    sup.append(link);
                }
                else {
                    sup.textContent = String(number);
                }
                parts.push(sup);
            }
            node.replaceWith(...parts);
        }
        return container;
    }
    // ------------------------------------------------------------ események --
    launcher.addEventListener("click", () => open());
    element("close").addEventListener("click", close);
    element("reset").addEventListener("click", reset);
    form.addEventListener("submit", (event) => {
        event.preventDefault();
        void send();
    });
    input.addEventListener("keydown", (event) => {
        if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
            event.preventDefault();
            void send();
        }
    });
    // Az Esc a panelt zárja — és nem jut el az oldal saját kezelőjéig (az a fiókot zárná).
    panel.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            event.stopPropagation();
            close();
        }
    });
    log.addEventListener("click", (event) => {
        const target = event.target;
        const ratingButton = target.closest("button[data-rating]");
        if (ratingButton) {
            const turnElement = ratingButton.closest(".cs-chat__turn");
            const answer = [...rendered.entries()].find(([, element]) => element === turnElement)?.[0];
            if (answer && isAnswer(answer)) {
                void sendFeedback(answer, ratingButton.dataset.rating === "down" ? "down" : "up");
            }
            return;
        }
        // A források és a citációk a súgó saját lapjaira mutatnak: a felületen belül nyílnak.
        const link = target.closest("a[href]");
        if (!link || classifyLink(link.getAttribute("href"), window.location.origin) !== "internal") {
            return;
        }
        const route = parseHelpRoute(link.href, window.location.origin);
        const context = host.getContext();
        if (!route?.page || !route.version || (route.product && context && route.product !== context.product)) {
            return;
        }
        event.preventDefault();
        host.navigate({ version: route.version, page: route.page, anchor: route.anchor });
        if (window.matchMedia(fullScreenQuery).matches) {
            close();
        }
    });
    for (const entry of conversation.entries) {
        renderEntry(entry);
    }
    refreshContext();
    return {
        open,
        refreshContext() {
            if (isOpen()) {
                refreshContext();
            }
        },
    };
}
